/**
 * The round result reveal.
 *
 * This is the educational payload. The player just spent eight seconds caring
 * about one country, which is the only moment a fact about it will actually
 * stick — so one fact goes on screen, briefly, and then the game moves on. Any
 * more than one and it reads as a lesson instead of a reward.
 */

import type { RoundEndMessage } from '@geogame/shared';
import { byCca3 } from '@geogame/data';

import { points, roundLine } from '../lib/format';

/**
 * THE FACT COMES FROM THE SERVER (invariant 5).
 *
 * This component used to build the line itself out of `@geogame/data`, which
 * meant the browser held every capital, population, border list and language
 * table it could ever need — for two hundred countries, including the one the
 * next round is about. A regex over the shipped bundle recovered the lot. The
 * card is worth keeping; holding the answer key to render it is not, so
 * `round.end` now carries the one sentence and `MatchRoom.roundFact` builds it.
 */

export interface RoundRevealProps {
  readonly result: RoundEndMessage;
  readonly opponentHandle: string;
  /** How many rungs this tier's ladder has. `roundLine` counts questions off it. */
  readonly ladderDepth: number;
}

export function RoundReveal({
  result,
  opponentHandle,
  ladderDepth,
}: RoundRevealProps): React.JSX.Element {
  const country = byCca3(result.you.targetCca3);
  const fact = result.fact?.line ?? null;

  return (
    <section className="gg-reveal" role="status">
      <div className="flex items-center gap-3">
        <span className="text-3xl leading-none" aria-hidden="true">
          {country?.flagEmoji ?? '🌍'}
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-xl font-bold leading-tight">
            {result.targetLabel ?? country?.name ?? result.fact?.name ?? result.you.targetCca3}
          </h2>
          {fact ? <p className="gg-reveal-fact">{fact}</p> : null}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="gg-reveal-side" data-banked={result.you.banked ? 'true' : 'false'}>
          <span className="gg-reveal-who">You</span>
          <span className="gg-reveal-pot tabular-nums">
            {result.you.banked ? `+${points(result.you.pot)}` : points(0)}
          </span>
          <span className="gg-reveal-outcome">
            {roundLine(result.you.outcome, result.you.finalRung, ladderDepth)}
          </span>
        </div>
        <div
          className="gg-reveal-side text-right"
          data-banked={result.opp.banked ? 'true' : 'false'}
        >
          <span className="gg-reveal-who truncate">{opponentHandle}</span>
          <span className="gg-reveal-pot tabular-nums">
            {result.opp.banked ? `+${points(result.opp.pot)}` : points(0)}
          </span>
          <span className="gg-reveal-outcome">
            {roundLine(result.opp.outcome, result.opp.finalRung, ladderDepth)}
          </span>
        </div>
      </div>
    </section>
  );
}
